import qs from "qs";
import { soddApi } from "./soddApi";

function formatParams(params) {
    return qs.stringify(params, {
        arrayFormat: "brackets",
        skipNulls: true,
        filter: (prefix, value) => value || undefined,
    });
}

export const strategicPlanningApi = soddApi
    .enhanceEndpoints({
        addTagTypes: ["plan", "objective", "goal", "action-plan", "project", "kpi"],
    })
    .injectEndpoints({
        endpoints: (build) => ({
            /**
             * plan
             */
            getAllPlan: build.query({
                query: () => ({ url: "api/v1/strategic-plans" }),
                providesTags: (result) => {
                    return [
                        { type: "plan", id: "*" },
                        ...result?.data?.map(({ id }) => ({
                            type: "plan",
                            id,
                        })),
                    ];
                },
            }),
            getPlanById: build.query({
                query: ({ id }) => ({ url: `api/v1/strategic-plans/${id}` }),
                providesTags: (result) => {
                    return [{ type: "plan", id: result?.data?.id }];
                },
            }),
            createPlan: build.mutation({
                query: ({ data }) => ({
                    url: `api/v1/strategic-plans`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: [{ type: "plan", id: "*" }],
            }),
            updatePlanById: build.mutation({
                query: ({ id, data }) => ({
                    url: `api/v1/strategic-plans/${id}/update`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: (result) => {
                    return [{ type: "plan", id: result?.data?.id ?? "*" }];
                },
            }),
            deletePlanById: build.mutation({
                query: ({ id }) => ({
                    url: `api/v1/strategic-plans/${id}/delete`,
                    method: "POST",
                }),
                invalidatesTags: (result, error, arg) => {
                    return [{ type: "plan", id: arg.id }];
                },
            }),
            /**
             * objective
             */
            getAllObjective: build.query({
                query: () => ({
                    url: "api/v1/strategic-objectives",
                }),
                providesTags: (result) => {
                    return [
                        { type: "objective", id: "*" },
                        ...result?.data?.map(({ id }) => ({
                            type: "objective",
                            id,
                        })),
                    ];
                },
            }),
            getObjectiveById: build.query({
                query: ({ id }) => ({
                    url: `api/v1/strategic-objectives/${id}`,
                }),
                providesTags: (result) => {
                    return [{ type: "objective", id: result?.data?.id }];
                },
            }),
            createObjective: build.mutation({
                query: ({ data }) => ({
                    url: `api/v1/strategic-objectives`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        {
                            type: "plan",
                            id: arg.planId ?? result?.data?.strategic_plan_id ?? "*",
                        },
                        { type: "objective", id: "*" },
                    ];
                },
            }),
            updateObjectiveById: build.mutation({
                query: ({ id, data }) => ({
                    url: `api/v1/strategic-objectives/${id}/update`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        {
                            type: "plan",
                            id: arg.planId ?? result?.data?.strategic_plan_id ?? "*",
                        },
                        { type: "objective", id: result?.data?.id ?? "*" },
                    ];
                },
            }),
            deleteObjectiveById: build.mutation({
                query: ({ id }) => ({
                    url: `api/v1/strategic-objectives/${id}/delete`,
                    method: "POST",
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        { type: "plan", id: arg.planId ?? "*" },
                        { type: "objective", id: arg.id },
                    ];
                },
            }),
            /**
             * goal
             */
            getAllGoal: build.query({
                query: () => ({
                    url: "api/v1/strategic-goals",
                }),
                providesTags: (result) => {
                    return [
                        { type: "goal", id: "*" },
                        ...result?.data?.map(({ id }) => ({
                            type: "goal",
                            id,
                        })),
                    ];
                },
            }),
            getGoalById: build.query({
                query: ({ id }) => ({
                    url: `api/v1/strategic-goals/${id}`,
                }),
                providesTags: (result) => {
                    return [{ type: "goal", id: result?.data?.id }];
                },
            }),
            createGoal: build.mutation({
                query: ({ data }) => ({
                    url: `api/v1/strategic-goals`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        { type: "plan", id: arg.planId ?? "*" },
                        { type: "goal", id: "*" },
                    ];
                },
            }),
            updateGoalById: build.mutation({
                query: ({ id, data }) => ({
                    url: `api/v1/strategic-goals/${id}/update`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        { type: "plan", id: arg.planId ?? "*" },
                        { type: "goal", id: result?.data?.id ?? "*" },
                    ];
                },
            }),
            deleteGoalById: build.mutation({
                query: ({ id }) => ({
                    url: `api/v1/strategic-goals/${id}/delete`,
                    method: "POST",
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        { type: "plan", id: arg.planId ?? "*" },
                        { type: "goal", id: arg.id },
                    ];
                },
            }),
            /**
             * action plan
             */
            getAllActionPlan: build.query({
                query: () => ({
                    url: "api/v1/strategic-action-plans",
                }),
                providesTags: (result) => {
                    return [
                        { type: "action-plan", id: "*" },
                        ...result?.data?.map(({ id }) => ({
                            type: "action-plan",
                            id,
                        })),
                    ];
                },
            }),
            getActionPlanById: build.query({
                query: ({ id }) => ({
                    url: `api/v1/strategic-action-plans/${id}`,
                }),
                providesTags: (result) => {
                    return [{ type: "action-plan", id: result?.data?.id }];
                },
            }),
            createActionPlan: build.mutation({
                query: ({ data }) => ({
                    url: `api/v1/strategic-action-plans`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        { type: "plan", id: arg.planId ?? "*" },
                        { type: "action-plan", id: "*" },
                    ];
                },
            }),
            updateActionPlanById: build.mutation({
                query: ({ id, data }) => ({
                    url: `api/v1/strategic-action-plans/${id}/update`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        { type: "plan", id: arg.planId ?? "*" },
                        { type: "action-plan", id: result?.data?.id ?? "*" },
                    ];
                },
            }),
            deleteActionPlanById: build.mutation({
                query: ({ id }) => ({
                    url: `api/v1/strategic-action-plans/${id}/delete`,
                    method: "POST",
                }),
                invalidatesTags: (result, error, arg) => {
                    return [
                        { type: "plan", id: arg.planId ?? "*" },
                        { type: "action-plan", id: arg.id },
                    ];
                },
            }),
        }),
    });
